// Ronin Box — helpers (cue libraries now live in MongoDB, fetched from /api/ronin-box/cues).
// This file only contains pure helpers (audio, mode metadata, formatting, fallback intensity defaults).

// ── Intensity defaults (used until the API loads) ──────────────────────────
export const INTENSITIES = [
  { id: "slow",   label: "Slow",   min_ms: 3500, max_ms: 4500 },
  { id: "medium", label: "Medium", min_ms: 2400, max_ms: 3400 },
  { id: "fast",   label: "Fast",   min_ms: 1400, max_ms: 2200 },
  { id: "blitz",  label: "Blitz",  min_ms: 800,  max_ms: 1400 },
];

export function nextCueDelayMs(intensityId, intensities = INTENSITIES) {
  const cfg = intensities.find((i) => i.id === intensityId) || intensities[1] || INTENSITIES[1];
  return Math.floor(cfg.min_ms + Math.random() * (cfg.max_ms - cfg.min_ms));
}

// ── Per-cue time-to-complete ───────────────────────────────────────────────
//
// Old behavior: every cue had the same intensity-based gap, regardless of whether it
// was a 1-punch jab or a 4-punch combo. The coach asked for per-cue timing so a longer
// combo gets more time to land — same idea for footwork/defense (more steps = more time).
//
// Defaults at MEDIUM intensity (non-linear — diminishing returns past 4 punches):
//   Punches (count from cue.punches):
//     1=1.7s  2=2.4s  3=3.0s  4=3.6s  5=4.0s  6=4.4s  7=4.7s  8+=5.0s
//   Movement (cue.movement_count — footwork/defense pieces):
//     1=1.6s  2=2.3s  3=2.9s  4=3.4s  5+=3.8s
//   Non-counted categories (feint, range, atmosphere, or cues with 0 punches+0 movement): 2.0s base
//
// Intensity acts as a global multiplier so the coach can still speed up / slow down the whole
// session without re-editing every cue.
//   slow→1.35  medium→1.0  fast→0.72  blitz→0.55
//
// Per-cue override (`cue.cue_seconds`) takes precedence over the punch/movement defaults but
// still gets the intensity multiplier applied — that way an admin can set an absolute base
// time and still respect intensity. Jitter ±150ms keeps things from feeling robotic.

const _PUNCH_BASE_SECS = [0, 1.7, 2.4, 3.0, 3.6, 4.0, 4.4, 4.7, 5.0]; // index = punch count
const _MOVE_BASE_SECS = [0, 1.6, 2.3, 2.9, 3.4, 3.8];                 // index = movement count
const _DEFAULT_BASE_SECS = 2.0;

const _INTENSITY_MULTIPLIER = {
  slow: 1.35, medium: 1.0, fast: 0.72, blitz: 0.55,
};

export function cueBaseSeconds(cue) {
  if (!cue) return _DEFAULT_BASE_SECS;
  // Explicit override wins
  if (Number.isFinite(cue.cue_seconds) && cue.cue_seconds > 0) {
    return Math.max(0.5, Math.min(15, Number(cue.cue_seconds)));
  }
  const punches = Math.max(0, Math.floor(Number(cue.punches) || 0));
  if (punches > 0) {
    return _PUNCH_BASE_SECS[Math.min(punches, _PUNCH_BASE_SECS.length - 1)];
  }
  const moves = Math.max(0, Math.floor(Number(cue.movement_count) || 0));
  if (moves > 0) {
    return _MOVE_BASE_SECS[Math.min(moves, _MOVE_BASE_SECS.length - 1)];
  }
  return _DEFAULT_BASE_SECS;
}

export function cueGapMs(cue, intensityId) {
  const baseSec = cueBaseSeconds(cue);
  const mult = _INTENSITY_MULTIPLIER[intensityId] ?? 1.0;
  const jitterMs = Math.floor((Math.random() - 0.5) * 300); // ±150ms
  const ms = Math.max(500, Math.round(baseSec * mult * 1000) + jitterMs);
  return ms;
}

// Multi-tier level filter: a cue is visible if its `levels` array contains the selected level.
// A cue can belong to any combination of levels (e.g. ["beginner", "advanced"] but not "intermediate").
// `selectedLevel` of falsy / "all" disables the filter.
export const LEVEL_RANK = { beginner: 0, intermediate: 1, advanced: 2 };
export function cueMatchesLevel(cue, selectedLevel) {
  if (!selectedLevel || selectedLevel === "all") return true;
  const levels = (cue?.levels && cue.levels.length > 0)
    ? cue.levels
    // Defensive fallback: derive from legacy `level` (cumulative)
    : (cue?.level === "advanced" ? ["advanced"]
       : cue?.level === "intermediate" ? ["intermediate", "advanced"]
       : ["beginner", "intermediate", "advanced"]);
  return levels.includes(selectedLevel);
}

// ── Build a weighted random pool from a flat cue list ──────────────────────
//
// Inputs: cues = [{text, category, punches, weight, modes, level, audio_url, ...}, ...]
//         mode = mode id (any string — "custom" is treated as wildcard)
//         modeCategoryWeights = {mode: {category: multiplier}}
//         selectedLevel = "beginner" | "intermediate" | "advanced" | undefined (defaults to all)
//
// Returns: { pool: [cue, cue, ...], atmospherePool: [cue, ...] }
export function buildCuePool({ cues, mode, modeCategoryWeights, selectedLevel }) {
  const main = [];
  const atmo = [];
  // "custom" mode is a wildcard — every cue is eligible; the user has already curated their
  // own list via the cue picker. Weighting still applies (balanced by default).
  const isCustom = mode === "custom";
  const catW = (modeCategoryWeights && modeCategoryWeights[mode]) || {};
  for (const c of cues || []) {
    if (!cueMatchesLevel(c, selectedLevel)) continue;
    const modeOK = isCustom || (c.modes || []).includes(mode);
    if (!modeOK && c.category !== "atmosphere") continue;
    const mult = isCustom ? 1 : (catW[c.category] ?? 1);
    if (mult <= 0 && c.category !== "atmosphere") continue;
    const repeats = Math.max(1, (Number(c.weight) || 1) * mult);
    const target = c.category === "atmosphere" ? atmo : main;
    for (let i = 0; i < repeats; i++) target.push(c);
  }
  return { pool: main, atmospherePool: atmo };
}

export function pickRandom(pool) {
  if (!pool || pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}

// ── Round sequencer (ratio-based) ──────────────────────────────────────────
//
// The coach wanted a coach-style rhythm instead of pure weighted random:
//   • Per-mode ratio: how many cues of each category fire per cycle (admin-configurable).
//     Defaults to 4 punches : 2 feints : 1 footwork : 1 defense (an 8-slot cycle).
//   • Order within the cycle is shuffled — only the ratio is locked.
//   • Empty categories are skipped (e.g. user unchecked all feints → that slot is dropped).
//   • Range cues fire 1 OR 2 times per round, separated by ≥60 seconds — independent of the ratio.
//   • Atmosphere cues are NOT scheduled here — they're sprinkled probabilistically by the
//     caller (matches the legacy atmosphere_chance setting) and pre-empt the sequencer.
//
// Usage:
//   const seq = createCueSequencer({ cues, mode, level, selectedCueIds, ratios });
//   seq.beginRound({ roundDurationSec: 180 });   // call before each round starts
//   const cue = seq.nextCue(elapsedMsInRound);   // returns cue object or null

const _DEFAULT_RATIO = { punch: 4, feint: 2, footwork: 1, defense: 1 };

function _shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Expand a {category: count} dict into a flat slot array, e.g. {punch:4, feint:2, defense:1}
// → ["punch","punch","punch","punch","feint","feint","defense"]. Categories with count ≤ 0
// are dropped. Range/atmosphere are silently filtered (they have their own handling).
function _ratioToTemplate(ratio) {
  const template = [];
  if (!ratio || typeof ratio !== "object") return template;
  for (const [cat, count] of Object.entries(ratio)) {
    if (cat === "range" || cat === "atmosphere") continue;
    const n = Math.max(0, Math.min(20, Math.floor(Number(count) || 0)));
    for (let i = 0; i < n; i++) template.push(cat);
  }
  return template;
}

export function createCueSequencer({ cues, mode, level, selectedCueIds, ratios, rangePerRound, sequences }) {
  // Apply the same eligibility filter the picker uses (mode + level + selectedCueIds).
  // Atmosphere is excluded — caller handles atmosphere separately.
  const isCustom = mode === "custom";
  const selSet = (selectedCueIds && selectedCueIds.length > 0) ? new Set(selectedCueIds) : null;
  const eligible = (cues || []).filter((c) => {
    if (c.category === "atmosphere") return false;
    if (selSet && !selSet.has(c.id)) return false;
    if (!isCustom && !(c.modes || []).includes(mode)) return false;
    if (!cueMatchesLevel(c, level)) return false;
    return true;
  });

  // Bucket by category for fast lookups
  const byCategory = {};
  for (const c of eligible) {
    const k = c.category || "punch";
    (byCategory[k] = byCategory[k] || []).push(c);
  }

  // Per-cue weight respected within a category (higher weight → bigger sub-pool inside its slot)
  const weightedSubPool = (cat) => {
    const list = byCategory[cat] || [];
    if (list.length === 0) return [];
    const pool = [];
    for (const c of list) {
      const w = Math.max(1, Math.min(10, Number(c.weight) || 1));
      for (let i = 0; i < w; i++) pool.push(c);
    }
    return pool;
  };

  // Resolve the ratio for this mode (falls back to defaults if admin didn't configure it).
  const modeRatio = (ratios && typeof ratios === "object" && ratios[mode]) || _DEFAULT_RATIO;
  const templateAll = _ratioToTemplate(modeRatio);
  // Range frequency for this mode. Accepts "random" | "off" | "1" | "2" | "3" (string OR number).
  // Normalize to one of: "random", 0, 1, 2, 3. "random" → roll 1 OR 2 per round.
  const rawRange = rangePerRound;
  let modeRange = "random";
  if (rawRange === undefined || rawRange === null || rawRange === "random") {
    modeRange = "random";
  } else if (rawRange === "off" || rawRange === 0 || rawRange === "0") {
    modeRange = 0;
  } else {
    const n = Math.max(0, Math.min(3, Math.floor(Number(rawRange) || 0)));
    modeRange = n;
  }

  let patternQueue = []; // active shuffled slot list ([category, category, ...])
  let rangeSchedule = []; // sorted ms timestamps when range cues should fire
  let rangeFired = 0;
  let lastCueId = null;
  let lastCategory = null;

  // ── Sequences (combos) ───────────────────────────────────────────────────
  // Each sequence is {id, name, cue_ids: [...], active}. The runner plays them in
  // order, treating each step as a normal cue (so per-cue timing/audio works).
  // Per-round cap: 3 firings per sequence. Probabilistic injection rate: 65% of cue
  // slots when an eligible sequence exists. Mid-sequence cues bypass the cap check.
  const SEQUENCE_FIRE_CHANCE = 0.65;
  const SEQUENCE_MAX_PER_ROUND = 3;
  const cueById = {};
  for (const c of eligible) cueById[c.id] = c;

  // Filter sequences down to ones that are firable: at least one cue and EVERY cue
  // exists in the eligible pool (so we never play a sequence that references a cue
  // the user hasn't selected / that's filtered out by level/mode).
  const allSequences = (sequences || []).filter((s) => {
    if (!s || !s.active) return false;
    if (!Array.isArray(s.cue_ids) || s.cue_ids.length === 0) return false;
    return s.cue_ids.every((id) => cueById[id]);
  });

  let sequenceQueue = []; // remaining cue objects from the active sequence (empty = not currently in a sequence)
  let sequenceCounts = {}; // sequenceId -> times fired this round
  let activeSequence = null; // the sequence object currently being played, or null

  const refillPattern = () => {
    // Drop slots whose category has no eligible cues entirely (keeps the ratio honest
    // by removing dead slots instead of cycling through them forever).
    const filtered = templateAll.filter((slot) => byCategory[slot] && byCategory[slot].length > 0);
    patternQueue = _shuffle(filtered);
  };

  const scheduleRange = (roundDurationSec) => {
    rangeSchedule = [];
    rangeFired = 0;
    const haveRange = (byCategory.range && byCategory.range.length > 0);
    if (!haveRange || modeRange === 0) return;
    // Decide target count
    let target;
    if (modeRange === "random") target = Math.random() < 0.5 ? 1 : 2;
    else target = modeRange; // 1, 2, or 3

    const dur = roundDurationSec * 1000;

    // 1 cue: any point ≥30s into the round, last firing has at least 30s remaining
    if (target === 1) {
      const earliest = 30 * 1000;
      const latest = dur - 30 * 1000;
      if (latest <= earliest) return; // round too short
      rangeSchedule.push(earliest + Math.random() * (latest - earliest));
      return;
    }

    // 2 cues: ≥60s apart, last one ≥30s before round end
    if (target === 2) {
      const gap = 60 * 1000;
      const tailGuard = 30 * 1000;   // last cue must fire with ≥30s left
      const headGuard = 30 * 1000;   // first cue must fire ≥30s in (matches the "1 cue" behavior)
      const earliestFirst = headGuard;
      const latestLast = dur - tailGuard;
      const span = latestLast - earliestFirst;
      if (span < gap) return; // round too short — drop the second one rather than violate
      // First fires in [earliestFirst, latestLast - gap], second is first + gap + extra slack
      const firstUpper = latestLast - gap;
      const t1 = earliestFirst + Math.random() * (firstUpper - earliestFirst);
      const t2 = t1 + gap + Math.random() * Math.max(0, latestLast - (t1 + gap));
      rangeSchedule.push(t1, t2);
      return;
    }

    // 3 cues: ≥40s apart, last ≥20s before round end
    if (target === 3) {
      const gap = 40 * 1000;
      const tailGuard = 20 * 1000;
      const headGuard = 20 * 1000;
      const earliestFirst = headGuard;
      const latestLast = dur - tailGuard;
      const span = latestLast - earliestFirst;
      // Need at least 2 gaps (80s) of room between t1 and t3
      if (span < 2 * gap) return; // round too short for 3 cues — silently degrade to 0 instead of breaking
      // Place t1 in [earliestFirst, latestLast - 2*gap], pick gaps with slack
      const slack = span - 2 * gap;
      const slack1 = Math.random() * slack;
      const t1 = earliestFirst + slack1;
      const slackLeft = slack - slack1;
      // Add a bit of randomness to the second and third gap separately
      const extra1 = Math.random() * slackLeft;
      const t2 = t1 + gap + extra1;
      const t3 = t2 + gap + Math.max(0, slackLeft - extra1);
      rangeSchedule.push(t1, t2, t3);
      return;
    }
  };

  return {
    beginRound({ roundDurationSec }) {
      patternQueue = [];
      lastCategory = null;
      lastCueId = null;
      sequenceQueue = [];
      sequenceCounts = {};
      activeSequence = null;
      scheduleRange(roundDurationSec);
    },

    peekNextSlot() { return patternQueue[0] || null; },

    /** Diagnostic — returns the sequence currently mid-firing, or null. Used by the
     *  admin preview to surface a "jump to edit sequence" button while a combo is playing. */
    getActiveSequence() { return activeSequence; },

    nextCue(elapsedMsInRound) {
      // 0) If we're mid-sequence, return the next step. Bypasses the 3/round cap
      //    (the cap is for STARTING a sequence, not finishing one already in-flight).
      if (sequenceQueue.length > 0) {
        const next = sequenceQueue.shift();
        // When the queue empties on this pop, the sequence is officially over
        if (sequenceQueue.length === 0) activeSequence = null;
        lastCueId = next.id;
        lastCategory = next.category || null;
        return next;
      }

      // 1) Range cue pre-emption
      if (rangeFired < rangeSchedule.length && elapsedMsInRound >= rangeSchedule[rangeFired]) {
        const pool = weightedSubPool("range");
        const cue = pickRandom(pool);
        if (cue) {
          rangeFired += 1;
          lastCategory = "range";
          lastCueId = cue.id;
          return cue;
        }
        rangeFired += 1;
      }

      // 2) Sequence pre-emption — pick a fresh sequence with high probability when one
      //    is eligible. "Eligible" = active + every cue in the picker pool + fired <3 times
      //    this round. Sequences are weighted-random across the eligible pool.
      const eligibleSeqs = allSequences.filter((s) => (sequenceCounts[s.id] || 0) < SEQUENCE_MAX_PER_ROUND);
      if (eligibleSeqs.length > 0 && Math.random() < SEQUENCE_FIRE_CHANCE) {
        const chosen = eligibleSeqs[Math.floor(Math.random() * eligibleSeqs.length)];
        sequenceCounts[chosen.id] = (sequenceCounts[chosen.id] || 0) + 1;
        // Hydrate the cue list now (id → cue object). First step returned immediately,
        // the rest go into sequenceQueue so subsequent nextCue() calls drain it in order.
        const stepCues = chosen.cue_ids.map((id) => cueById[id]).filter(Boolean);
        if (stepCues.length > 0) {
          const first = stepCues[0];
          sequenceQueue = stepCues.slice(1);
          // Single-step sequences end immediately; multi-step sequences stay active until queue drains
          activeSequence = sequenceQueue.length > 0 ? chosen : null;
          lastCueId = first.id;
          lastCategory = first.category || null;
          return first;
        }
      }

      // 3) Pattern slot
      if (patternQueue.length === 0) refillPattern();
      if (patternQueue.length === 0) return null; // no eligible non-range cues at all

      const cat = patternQueue.shift();
      const pool = weightedSubPool(cat);
      if (pool.length === 0) return this.nextCue(elapsedMsInRound);
      let cue = pickRandom(pool);
      if (cue && cue.id === lastCueId && pool.length > 1) {
        const c2 = pickRandom(pool);
        if (c2 && c2.id !== lastCueId) cue = c2;
      }
      if (!cue) return null;
      lastCategory = cat;
      lastCueId = cue.id;
      return cue;
    },
  };
}

// ── Web Speech API helper (fallback if a cue has no audio) ─────────────────

let _voicesCache = null;
function getPreferredVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  if (_voicesCache === null) _voicesCache = window.speechSynthesis.getVoices();
  if (!_voicesCache || _voicesCache.length === 0) {
    _voicesCache = window.speechSynthesis.getVoices();
  }
  const prefs = [/Daniel/i, /Alex/i, /Fred/i, /Google US English/i, /Microsoft David/i, /en[-_]US/i];
  for (const re of prefs) {
    const v = _voicesCache?.find((x) => re.test(x.name) || re.test(x.lang));
    if (v) return v;
  }
  return _voicesCache?.[0] || null;
}

export function speak(text, { rate = 1.05, pitch = 0.95, volume = 1.0 } = {}) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
  const u = new SpeechSynthesisUtterance(text);
  const v = getPreferredVoice();
  if (v) u.voice = v;
  u.rate = rate; u.pitch = pitch; u.volume = volume;
  try { window.speechSynthesis.speak(u); } catch { /* ignore */ }
}

export function cancelSpeech() {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
}

export function primeSpeech() {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  try {
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0; u.rate = 1;
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

// ── Audio beeps (countdown ticks + bell) — Web Audio API ───────────────────

let _ctx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ctx = new AC();
  return _ctx;
}

export function beep({ freq = 880, ms = 120, type = "sine", gain = 0.25 } = {}) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type; o.frequency.value = freq;
    g.gain.value = gain;
    o.connect(g); g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + ms / 1000);
  } catch { /* ignore */ }
}

export function bell() {
  beep({ freq: 720, ms: 180, type: "triangle", gain: 0.35 });
  setTimeout(() => beep({ freq: 540, ms: 220, type: "triangle", gain: 0.35 }), 90);
}

export function tick() {
  beep({ freq: 660, ms: 60, type: "square", gain: 0.18 });
}

// ── Stats / estimates ──────────────────────────────────────────────────────

export function estimateCalories({ activeSeconds, intensityId }) {
  const rates = { slow: 6, medium: 9, fast: 11, blitz: 13 };
  const kcalPerMin = rates[intensityId] || 9;
  return Math.round((activeSeconds / 60) * kcalPerMin);
}

export function formatTime(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ── Mode metadata (static — display only) ──────────────────────────────────
export const MODES = [
  { id: "custom",  label: "Custom",        tagline: "You pick the cues",
    description: "Build your own workout. After you press start, pick exactly which cues you want — punches, defense, footwork, feints, atmosphere — and roll your own session." },
  { id: "shadow",  label: "Shadow Boxing", tagline: "Punches · defense · footwork · feints",
    description: "Full corner experience — punches, slips, rolls, pivots, and L-steps. Perfect for warm-up, road work, or hotel-room sessions." },
  { id: "circuit", label: "Circuit", tagline: "Shadowboxing + exercise breaks", icon: "Activity",
    description: "Shadowbox with timed exercise breaks mixed in — burpees, sit-ups, push-ups and more. Set how many breaks per round and how long they last." },
  { id: "bag",     label: "Bag Work", tagline: "Punches with power cues",
    description: "Heavier weighting on punches with power qualifiers (light, stiff, snap, heavy). Built for the heavy bag or pads." },
  { id: "defense", label: "Defense Mode", tagline: "React to incoming punches",
    description: "The voice calls a punch — you decide whether to slip, parry, roll, block, or weave. Trains reaction over execution." },
];

export function modeById(id) {
  return MODES.find((m) => m.id === id) || MODES[0];
}
